import React from 'react';

import { createDocumentRoute, parseMarkdownLocationLink } from '@aline/common';
import { useScrollToLocationForReference } from 'src/editor';
import { openLink, useDispatchNotification, useMaybeDocumentParams } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';

import { Citation } from '../../../../shared/components/Citation';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { createAIChatItem } from '../../helpers/aiChatItem';
import { parseLocation } from '../../helpers/parseLocation';

interface AIChatCitationProps {
  citationIndex: number;
  href: string;
  messageId: string;
  referencedText: string;
  threadId: string;
}

/**
 * Container component specifically for AI chat citations.
 * This component handles the business logic for AI chat citations,
 * then renders the presentational Citation component.
 */
export const AIChatCitation = React.memo(function AIChatCitation({
  citationIndex,
  href,
  messageId,
  referencedText,
  threadId,
  ...rest
}: AIChatCitationProps & MarkdownComponentProps<HTMLAnchorElement>): JSX.Element | null {
  const editor = useMaybeEditor();
  const { documentId } = useMaybeDocumentParams();
  const { scrollToLocationForReference } = useScrollToLocationForReference();
  const dispatchLocationNotFoundNotification = useDispatchNotification({
    message:
      'Document location not found. This can occur if the document has been updated since the citation was generated.',
    variant: 'warning',
  });
  const parts = parseMarkdownLocationLink(href);
  if (!parts) return null;

  const elementId = createAIChatItem({
    index: citationIndex,
    messageId,
    threadId,
    type: 'citation',
  });
  const handleClickCitation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const isCitationInDocument = parts.documentId === documentId;
    if (!isCitationInDocument) {
      return openLink(
        createDocumentRoute(parts.documentId, {
          aiChatItem: createAIChatItem({
            index: citationIndex,
            messageId,
            threadId,
            type: 'citation',
          }),
        })
      );
    }

    const aiReference = editor ? parseLocation(href, referencedText, editor) : null;
    if (!aiReference) return dispatchLocationNotFoundNotification();

    scrollToLocationForReference({ aiReference });
  };

  /**
   * Important: {...rest} must be first so that id is correct and not overridden.
   * data-citation-href and data-referenced-text are read by useScrollToChatItemEffect
   * to scroll the document canvas when navigating from /chat to the editor.
   */
  return (
    <Citation
      {...rest}
      data-citation-href={href}
      data-referenced-text={referencedText}
      id={elementId}
      onClickCitation={handleClickCitation}
    />
  );
});
